// Phase 9A.3 — Shared formatting helpers for curated public signal cards.
//
// Pure deterministic helpers shared by PublicSignalCardItem and the
// new PublicSignalDetailDrawer. NO React, NO Supabase, NO browser
// APIs, NO clock reads, NO side effects. Every helper is module-pure
// and returns a string.
//
// These helpers operate on the public-safe `PublicSignalCardViewModel`
// surface only — they CANNOT touch evidence_payload, raw_payload,
// grade evidence, ledger evidence, closing-line evidence, or provider
// evidence because those fields are not on the view-model surface.

export function ratioToPct(n: number | null): number | null {
  return n === null || !Number.isFinite(n) ? null : n * 100;
}

export function formatTier(tier: number | null): string {
  if (tier === null) return "—";
  return `Tier ${String(tier)}`;
}

export function formatDataTierCopy(tier: number | null): string {
  if (tier === null) return "Data tier —";
  return `Data tier ${String(tier)}`;
}

export function formatGeneratedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `Prepared ${d.toUTCString()}`;
}

export function formatExpiresAt(iso: string | null): string {
  if (iso === null) return "No expiry recorded";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `Expires ${d.toUTCString()}`;
}

export function formatCandidateRank(rank: number | null): string {
  if (rank === null) return "—";
  return `Rank ${String(rank)}`;
}

// Phase 9A.5 — shared save-button label.
export function watchlistSaveLabel(saved: boolean, busy: boolean): string {
  if (busy) return "Working…";
  return saved ? "Saved" : "Save signal";
}

// Phase 9A.5 — shared restrained save-error copy used by both card
// surfaces if a mutation fails.
export const SAVE_MUTATION_ERROR_COPY = "Couldn’t update watchlist. Try again.";

// Phase 9A.6 — saved-card private note helpers.
//
// `private_note` is a per-user, per-card scratchpad attached to the
// 9A.4 signal_card_watchlist row. It is NOT a comment, NOT a public
// surface, NOT shared. The DB enforces a 500-char CHECK; the helpers
// below normalize the client value before submission so the verifier
// can prove trim + empty-clears + length-cap behavior in pure
// function tests.

export const PRIVATE_NOTE_MAX_LENGTH = 500;

export interface NormalizedNote {
  // The value to write to the DB. `null` means "clear the note".
  value: string | null;
  // True if the input exceeded PRIVATE_NOTE_MAX_LENGTH and was rejected.
  tooLong: boolean;
}

export function normalizePrivateNote(input: string): NormalizedNote {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { value: null, tooLong: false };
  if (trimmed.length > PRIVATE_NOTE_MAX_LENGTH) {
    return { value: null, tooLong: true };
  }
  return { value: trimmed, tooLong: false };
}

export function privateNoteCharsRemaining(input: string): number {
  return PRIVATE_NOTE_MAX_LENGTH - input.length;
}

// Phase 9A.7 — saved-card private tag helpers.
//
// `private_tags` is a per-user, per-saved-card organization surface.
// NOT public. NOT social. NOT shared. The DB enforces:
//   - cardinality(private_tags) <= 5
//   - char_length(tag) <= 24 for every element
//   - no NULL / empty / whitespace-only elements
// The helpers below normalize client input (trim + lowercase + dedupe
// + drop empties) so the hook has a single source of truth before
// submission and the verifier can prove behavior in pure tests.

export const PRIVATE_TAGS_MAX_COUNT = 5;
export const PRIVATE_TAG_MAX_LENGTH = 24;

export interface NormalizedTags {
  // Cleaned, deduped, lowercased tags ready for the DB.
  value: string[];
  // True if any *non-empty* input tag exceeded PRIVATE_TAG_MAX_LENGTH.
  tooLong: boolean;
  // True if the cleaned set exceeded PRIVATE_TAGS_MAX_COUNT.
  tooMany: boolean;
}

export function normalizePrivateTags(input: readonly string[]): NormalizedTags {
  let tooLong = false;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    if (typeof raw !== "string") continue;
    const trimmed = raw.trim().toLowerCase();
    if (trimmed.length === 0) continue;
    if (trimmed.length > PRIVATE_TAG_MAX_LENGTH) {
      tooLong = true;
      continue;
    }
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  const tooMany = out.length > PRIVATE_TAGS_MAX_COUNT;
  return { value: out, tooLong, tooMany };
}

export function parseTagInputString(raw: string): string[] {
  // Split on commas, newlines, and tabs. Whitespace-trim each piece.
  // The normalizer downstream takes care of lowercase / dedupe /
  // drop-empties / length cap.
  return raw.split(/[\n\t,]+/).map((p) => p.trim());
}

// Doctrine string used in card + drawer footers.
export const MANUAL_PLACEMENT_REMINDER =
  "Manual placement only. STRIKZA does not connect to bookmaker accounts or place bets.";

export const PROBABILITY_VIEW_NOTE =
  "Probability view only. Not a bookmaker offer. Not a tradeable market. No automation.";
